import axios, { Canceler } from 'axios';
import { sortBy } from 'lodash';
import React from 'react';

import { Checkbox, LoadingPlaceholder } from '@dndbeyond/character-components/es';
import {
    ApiAdapterPromise,
    ApiAdapterRequestConfig,
    ApiAdapterUtils,
    ApiResponse,
    BaseItemDefinitionContract,
    Container,
    ContainerUtils,
    CharacterTheme,
    Item,
    ItemUtils,
    Modifier,
    RuleData,
    TypeValueLookup,
    PartyInfo,
} from '@dndbeyond/character-rules-engine/es';

import { AppLoggerUtils, FilterUtils } from '../../utils';
import { ThemeButton } from '../common/Button';
import { EquipmentShopItem } from './EquipmentShopItem';

interface Props {
    items?: Array<Item>;
    loadItems?: (
        additionalConfig?: Partial<ApiAdapterRequestConfig>,
    ) => ApiAdapterPromise<ApiResponse<Array<BaseItemDefinitionContract>>>;
    globalModifiers: Array<Modifier>;
    valueLookupByType: TypeValueLookup;
    pageSize: number;
    onItemAdd?: (item: Item, amount: number, containerDefinitionKey: string) => void;
    ruleData: RuleData;
    proficiencyBonus: number;
    containers: Array<Container>;
    theme: CharacterTheme;
    limitAddToCurrentContainer: Container | null;
    partyInfo: PartyInfo | null;
}
interface State {
    query: '';
    lazyItems: Array<Item>;
    filteredItems: Array<Item>;
    currentPage: number;
    loading: boolean;
    loaded: boolean;
    filterTypes: Array<string>;
    filterQuery: string;
    filterProficient: boolean;
    filterBasic: boolean;
    filterMagic: boolean;
    filterContainer: boolean;
}
export default class EquipmentShop extends React.PureComponent<Props, State> {
    static defaultProps = {
        pageSize: 12,
        limitAddToCurrentContainer: null,
    };

    loadItemsCanceler: null | Canceler = null;

    constructor(props: Props) {
        super(props);

        this.state = {
            query: '',
            lazyItems: [],
            filteredItems: props.items ? props.items : [],
            currentPage: 0,
            loading: false,
            loaded: false,
            filterTypes: [],
            filterQuery: '',
            filterProficient: false,
            filterBasic: false,
            filterMagic: false,
            filterContainer: false,
        };
    }

    componentDidMount() {
        const { loadItems } = this.props;

        if (loadItems) {
            this.setState({
                loading: true,
            });

            loadItems({
                cancelToken: new axios.CancelToken((c) => {
                    this.loadItemsCanceler = c;
                }),
            })
                .then((response) => {
                    let data = ApiAdapterUtils.getResponseData(response);
                    let transformedItems: Array<Item> = [];
                    if (data) {
                        transformedItems = this.transformLoadedItems(data.filter((item) => item.canBeAddedToInventory));
                    }
                    this.setState({
                        lazyItems: transformedItems,
                        loaded: true,
                        loading: false,
                    });
                    this.loadItemsCanceler = null;
                })
                .catch(AppLoggerUtils.handleAdhocApiError);
        } else {
            this.setState({
                loaded: true,
            });
        }
    }

    componentWillUnmount(): void {
        if (this.loadItemsCanceler !== null) {
            this.loadItemsCanceler();
        }
    }

    getCombinedItems = (): Array<Item> => {
        const { lazyItems } = this.state;

        return sortBy(lazyItems, [(item) => ItemUtils.getRarityLevel(item), (item) => ItemUtils.getName(item)]);
    };

    isFiltered = (): boolean => {
        const { filterQuery, filterTypes, filterProficient, filterMagic, filterBasic, filterContainer } = this.state;

        if (filterProficient || filterMagic || filterBasic || filterContainer) {
            return true;
        }

        if (filterQuery) {
            return true;
        }

        if (filterTypes.length) {
            return true;
        }

        return false;
    };

    getFilteredItems = (combinedItems: Array<Item>): Array<Item> => {
        const { filterQuery, filterTypes, filterProficient, filterMagic, filterBasic, filterContainer } = this.state;

        return combinedItems.filter((item) => {
            if (
                filterProficient &&
                (ItemUtils.isWeaponContract(item) || ItemUtils.isArmorContract(item)) &&
                !item.proficiency
            ) {
                return false;
            }

            let filterType = ItemUtils.getDefintionFilterType(item);
            if (filterTypes.length !== 0 && filterType !== null && !filterTypes.includes(filterType)) {
                return false;
            }

            let searchTags: Array<string> = [];
            if (ItemUtils.isGearContract(item)) {
                let subType = ItemUtils.getSubType(item);
                if (subType) {
                    searchTags.push(subType);
                }
            }
            if (
                filterQuery !== '' &&
                !FilterUtils.doesQueryMatchData(filterQuery, ItemUtils.getName(item), searchTags)
            ) {
                return false;
            }

            if (filterMagic && !filterBasic && !ItemUtils.isMagic(item)) {
                return false;
            }

            if (filterBasic && !filterMagic && ItemUtils.isMagic(item)) {
                return false;
            }

            if (filterContainer && !filterBasic && !filterMagic && !ItemUtils.isContainer(item)) {
                return false;
            }

            return true;
        });
    };

    transformLoadedItems = (data: Array<BaseItemDefinitionContract>): Array<Item> => {
        const { globalModifiers, valueLookupByType, ruleData } = this.props;

        return data.map((definition) =>
            ItemUtils.simulateItem(definition, globalModifiers, valueLookupByType, ruleData),
        );
    };

    getLastPageIdx = (filteredItems: Array<Item>): number => {
        const { pageSize } = this.props;

        return Math.ceil(filteredItems.length / pageSize) - 1;
    };

    handlePageMore = (): void => {
        this.setState((prevState) => ({
            currentPage: prevState.currentPage + 1,
        }));
    };

    handleQueryChange = (evt: React.ChangeEvent<HTMLInputElement>): void => {
        this.setState({
            filterQuery: evt.target.value,
            currentPage: 0,
        });
    };

    handleFilterItemType = (type: string): void => {
        this.setState((prevState: State) => ({
            filterTypes: prevState.filterTypes.includes(type)
                ? prevState.filterTypes.filter((t) => t !== type)
                : [...prevState.filterTypes, type],
            currentPage: 0,
        }));
    };

    handleProficientToggle = (): void => {
        this.setState((prevState: State) => ({
            filterProficient: !prevState.filterProficient,
            currentPage: 0,
        }));
    };

    handleBasicToggle = (): void => {
        this.setState((prevState: State) => ({
            filterBasic: !prevState.filterBasic,
            currentPage: 0,
        }));
    };

    handleMagicToggle = (): void => {
        this.setState((prevState: State) => ({
            filterMagic: !prevState.filterMagic,
            currentPage: 0,
        }));
    };

    handleContainerToggle = (): void => {
        this.setState((prevState: State) => ({
            filterContainer: !prevState.filterContainer,
            currentPage: 0,
        }));
    };

    renderFilterUi = (): React.ReactNode => {
        const { filterQuery, filterTypes, filterBasic, filterMagic, filterProficient, filterContainer } = this.state;

        const itemTypes: Array<string> = [
            'Armor',
            'Potion',
            'Ring',
            'Rod',
            'Scroll',
            'Staff',
            'Wand',
            'Weapon',
            'Wondrous item',
            'Other Gear',
        ];

        return (
            <div className="ct-equipment-shop__filters">
                <div className="ct-equipment-shop__filter">
                    <label className="ct-equipment-shop__filter-heading" htmlFor="equipment-shop-filter-input">
                        Filter
                    </label>
                    <input
                        id="equipment-shop-filter-input"
                        type="search"
                        className="ct-filter__query"
                        value={filterQuery}
                        onChange={this.handleQueryChange}
                        placeholder="Weapon, Longsword, Vorpal Longsword, etc."
                        spellCheck={false}
                        autoComplete="off"
                    />
                </div>
                <div className="ct-equipment-shop__filter">
                    <div className="ct-equipment-shop__filter-heading">Filter By Type</div>
                    <div className="ct-equipment-shop__levels">
                        {itemTypes.map((itemType) => (
                            <div className="ct-equipment-shop__level" key={itemType}>
                                <ThemeButton
                                    style={filterTypes.includes(itemType) ? '' : 'outline'}
                                    onClick={this.handleFilterItemType.bind(this, itemType)}
                                    block={true}
                                    size="small"
                                >
                                    {itemType === 'Wondrous item' ? 'Wondrous' : itemType}
                                </ThemeButton>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="ct-equipment-shop__filters-toggles">
                    <div className="ct-equipment-shop__filter-toggle">
                        <Checkbox
                            stopPropagation={true}
                            initiallyEnabled={filterProficient}
                            onChange={this.handleProficientToggle}
                            label="Proficient"
                        />
                    </div>
                    <div className="ct-equipment-shop__filter-toggle">
                        <Checkbox
                            stopPropagation={true}
                            initiallyEnabled={filterBasic}
                            onChange={this.handleBasicToggle}
                            label="Common"
                        />
                    </div>
                    <div className="ct-equipment-shop__filter-toggle">
                        <Checkbox
                            stopPropagation={true}
                            initiallyEnabled={filterMagic}
                            onChange={this.handleMagicToggle}
                            label="Magical"
                        />
                    </div>
                    <div className="ct-equipment-shop__filter-toggle">
                        <Checkbox
                            stopPropagation={true}
                            initiallyEnabled={filterContainer}
                            onChange={this.handleContainerToggle}
                            label="Container"
                        />
                    </div>
                </div>
            </div>
        );
    };

    renderPager = (filteredItems: Array<Item>): React.ReactNode => {
        const { currentPage } = this.state;

        const lastPageIdx = this.getLastPageIdx(filteredItems);

        if (!this.isFiltered()) {
            return null;
        }

        if (lastPageIdx < 1 || currentPage >= lastPageIdx) {
            return null;
        }

        return (
            <div className="ct-equipment-shop__pager">
                <ThemeButton block={true} onClick={this.handlePageMore}>
                    Load More
                </ThemeButton>
            </div>
        );
    };

    renderPagedListing = (filteredItems: Array<Item>): React.ReactNode => {
        const { currentPage } = this.state;
        const {
            pageSize,
            onItemAdd,
            ruleData,
            proficiencyBonus,
            containers,
            theme,
            limitAddToCurrentContainer,
            partyInfo,
        } = this.props;

        if (!this.isFiltered()) {
            return null;
        }

        const pagedFilteredItems: Array<Item> = filteredItems.slice(0, (currentPage + 1) * pageSize);

        return (
            <div className="ct-equipment-shop__items">
                {pagedFilteredItems.length ? (
                    pagedFilteredItems.map((item, idx) => {
                        let filteredContainers = containers;
                        const isContainer = ItemUtils.isContainer(item);
                        const isPack = ItemUtils.isPack(item);
                        if (isContainer || isPack) {
                            filteredContainers = containers.filter(
                                (container) =>
                                    ContainerUtils.isCharacterContainer(container) ||
                                    ContainerUtils.isPartyContainer(container),
                            );
                        } else if (limitAddToCurrentContainer) {
                            filteredContainers = [limitAddToCurrentContainer];
                        }
                        return (
                            <EquipmentShopItem
                                containers={filteredContainers}
                                theme={theme}
                                item={item}
                                key={`${item.id}-${idx}`}
                                onItemAdd={onItemAdd}
                                ruleData={ruleData}
                                proficiencyBonus={proficiencyBonus}
                                partyInfo={partyInfo}
                            />
                        );
                    })
                ) : (
                    <div className="ct-equipment-shop__empty">No Results Found</div>
                )}
            </div>
        );
    };

    renderUi = (): React.ReactNode => {
        const combinedItems = this.getCombinedItems();
        const filteredItems = this.getFilteredItems(combinedItems);

        return (
            <React.Fragment>
                {this.renderFilterUi()}
                {this.renderPagedListing(filteredItems)}
                {this.renderPager(filteredItems)}
            </React.Fragment>
        );
    };

    renderLoading = (): React.ReactNode => {
        return <LoadingPlaceholder />;
    };

    render() {
        const { loaded, loading } = this.state;

        return (
            <div className="ct-equipment-shop">
                {loading && this.renderLoading()}
                {!loading && loaded && this.renderUi()}
            </div>
        );
    }
}
